import React, { useState } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import Sidebar from "@/components/Sidebar";
import MobileBottomNav from "@/components/MobileBottomNav";
import SchoolBranchSwitcher from "@/components/SchoolBranchSwitcher";
import UserAvatar from "@/components/UserAvatar";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import { LogOut, Menu } from "lucide-react";

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { allBranches } = useBranch();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Show the school/branch switcher when the user has more than one branch to choose from.
  // Always show for school_owner + sub_owner (they always manage at least one school).
  // For staff (branch_admin / special_access) we only show it when they have 2+ approved branches.
  const isOwner = user?.role === "school_owner" || user?.role === "sub_owner";
  const isStaff = user?.role === "branch_admin" || user?.role === "special_access";
  // Special-access users primarily work on mobile — they get a bottom nav
  // instead of the side bar and the layout switches to a phone-first
  // single-column without horizontal scroll bleed.
  const isSpecialAccess = user?.role === "special_access";
  const approvedBranchCount = (allBranches || []).filter((b) => b.status === "approved").length;
  const showSwitcher = isOwner || (isStaff && approvedBranchCount > 1);

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br from-[#F5F8FF] via-white to-[#E8F0FE] flex ${isSpecialAccess ? "overflow-x-hidden" : ""}`}>
      {/* desktop sidebar — hidden for special_access users (they use bottom nav) */}
      {!isSpecialAccess && (
        <div className="hidden lg:block sticky top-0 h-screen shrink-0" data-testid="desktop-sidebar">
          <Sidebar />
        </div>
      )}

      {/* mobile sidebar (Sheet) — also disabled for special_access */}
      {!isSpecialAccess && (
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="p-0 w-72 max-w-[85vw]" data-testid="mobile-sidebar">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SheetDescription className="sr-only">Main navigation menu</SheetDescription>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        {/* top bar */}
        <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-[#2563EB]/10">
          <div className="relative flex items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 h-16">
            <div className="flex items-center gap-3 min-w-0">
              {!isSpecialAccess && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden rounded-2xl"
                  onClick={() => setMobileOpen(true)}
                  data-testid="mobile-menu-btn"
                >
                  <Menu className="w-5 h-5" />
                </Button>
              )}
              <span className={`font-heading font-semibold text-lg text-foreground ${isSpecialAccess ? "" : "lg:hidden"}`}>
                School<span className="text-[#2563EB]">Boss</span>
              </span>
            </div>

            {/* Centered School/Branch switcher — owner always, staff only when multi-branch */}
            {showSwitcher && (
              <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                <div className="pointer-events-auto">
                  <SchoolBranchSwitcher />
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 shrink-0">
              {user?.role !== "parent" && <NotificationBell />}
              <div className="hidden sm:flex items-center gap-2.5">
                <div className="flex flex-col items-end">
                  <span className="font-heading text-sm font-semibold text-foreground leading-tight" data-testid="topbar-user-name">
                    {user?.name}
                  </span>
                  <span className="text-xs text-brand-inkMuted capitalize leading-tight">
                    {user?.role?.replace("_", " ")}
                  </span>
                </div>
                <UserAvatar name={user?.name} src={user?.photo_base64} size="md" />
              </div>
              {/* Special-access logs out from the More sheet to keep the header thumb-light */}
              {!isSpecialAccess && (
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="rounded-full border-[#2563EB]/30 text-[#1D4ED8] hover:bg-[#EFF6FF] hover:text-[#1D4ED8] shadow-sm"
                  data-testid="navbar-logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              )}
            </div>
          </div>
          {user?.role === "school_owner" && (
            <div className="md:hidden px-4 pb-3 -mt-1">
              <SchoolBranchSwitcher />
            </div>
          )}
          {isSpecialAccess && approvedBranchCount > 1 && (
            <div className="px-4 pb-3 -mt-1">
              <SchoolBranchSwitcher />
            </div>
          )}
        </header>

        <main className="flex-1 relative overflow-x-hidden">
          <div className={`relative mx-auto ${
            isSpecialAccess
              ? "max-w-full px-3 py-5 pb-28"  // tighter padding + room for bottom nav
              : "max-w-7xl px-4 sm:px-6 lg:px-10 py-8"
          }`}>
            {/* When used as a layout route (no children prop), render the
                matched child route. Falls back to `children` for any legacy
                callsite that still wraps content directly. */}
            {children ?? <Outlet />}
          </div>
        </main>
      </div>

      {isSpecialAccess && <MobileBottomNav />}
    </div>
  );
}
